import React from "react";
import { useParams, Link } from "react-router-dom";
import { HeartPulse, ArrowRight, CheckCircle2 } from "lucide-react";
import PageTitle from "../components/PageTitle";
import { services } from "../data/mock";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);

  if (!service) {
    return (
      <div>
        <PageTitle title="Service Not Found" crumbs={[{ to: "/expertise", label: "Expertise" }, { label: "Not Found" }]} />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <p className="text-gray-600 mb-6">The service you are looking for does not exist.</p>
          <Link to="/expertise" className="inline-flex items-center gap-2 bg-[#b81d24] text-white px-6 py-3 rounded-full font-bold">Back to Expertise <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </div>
    );
  }

  const others = services.filter((s) => s.slug !== service.slug).slice(0, 6);

  return (
    <div>
      <PageTitle title={service.title} crumbs={[{ to: "/expertise", label: "Expertise" }, { label: service.title }]} />
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-3 gap-10">
          <article className="lg:col-span-2">
            <div className="rounded-2xl overflow-hidden mb-8 shadow-md">
              <img
                src="https://images.pexels.com/photos/7578797/pexels-photo-7578797.jpeg"
                alt={service.title}
                className="w-full h-72 object-cover"
              />
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900 mb-4">{service.title}</h2>
            <p className="text-gray-600 leading-relaxed mb-4">{service.long}</p>
            <p className="text-gray-600 leading-relaxed mb-6">Dr. Mamtesh Gupta and her team use the latest equipment and a patient-first approach to deliver this service safely and effectively. Every plan is personalised based on the patient’s history, symptoms and risk profile.</p>
            <div className="grid sm:grid-cols-2 gap-4 mt-8">
              {["Personalised treatment plan", "Latest equipment", "Experienced cardiac team", "Comprehensive post-care"].map((b, i) => (
                <div key={i} className="flex items-start gap-3 bg-[#fbf6ee] rounded-xl p-4">
                  <CheckCircle2 className="w-5 h-5 text-[#b81d24] mt-0.5 flex-shrink-0" />
                  <span className="text-gray-800 font-semibold">{b}</span>
                </div>
              ))}
            </div>
            <div className="mt-10">
              <Link to="/contact" className="inline-flex items-center gap-2 bg-[#b81d24] hover:bg-[#9a1820] text-white font-bold px-6 py-3 rounded-full transition-colors">
                Book Appointment <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </article>
          <aside>
            <div className="bg-[#fbf6ee] rounded-2xl p-6 mb-6">
              <h3 className="font-extrabold text-xl text-gray-900 mb-4">Other Services</h3>
              <ul className="space-y-2">
                {others.map((s) => (
                  <li key={s.slug}>
                    <Link to={`/services/${s.slug}`} className="flex items-center justify-between bg-white rounded-xl px-4 py-3 hover:bg-[#b81d24] hover:text-white transition-colors group">
                      <span className="font-semibold flex items-center gap-2"><HeartPulse className="w-4 h-4 text-[#b81d24] group-hover:text-amber-300" /> {s.title}</span>
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-[#b81d24] to-[#7d1318] rounded-2xl p-6 text-white">
              <h3 className="font-extrabold text-xl mb-2">Need Help?</h3>
              <p className="text-white/85 text-sm mb-4">Talk to Dr. Mamtesh Gupta about your concerns. We’re here to help.</p>
              <Link to="/contact" className="inline-flex items-center gap-2 bg-amber-400 text-[#1a1a1a] font-bold px-5 py-2.5 rounded-full hover:bg-amber-300 transition-colors">
                Contact Now <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
